import { useState } from 'react'
import { useQuery } from '@apollo/client'

import useToast, { ToastType } from '../../../hooks/useToast'
import useToggle from '../../../hooks/useToggle'
import useTranslation from '../../../hooks/useTranslation'
import { HERO_FEED_SLUG, HERO_SLOT_COUNT, maxItemsForFeed } from '../constants'
import {
  contentFeedQuery,
  IContentFeedBoost,
  IContentFeedResponse
} from '../queries/getContentFeeds'
import useFeedBoosts from './useFeedBoosts'
import useFeedSettings from './useFeedSettings'

const SUCCESS_TOAST_MS = 3000
const ERROR_TOAST_MS = 8000

const useContentFeedDetail = (id?: string) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const {
    isOn: isSettingsOpen,
    turnOn: openSettings,
    turnOff: hideSettings
  } = useToggle()
  const {
    isOn: isSelectOpen,
    turnOn: openSelect,
    turnOff: closeSelect
  } = useToggle()

  const { data, loading, error } = useQuery<IContentFeedResponse>(
    contentFeedQuery,
    { variables: { id }, skip: !id }
  )
  const feed = data?.contentFeed ?? null

  const {
    boosts,
    loading: boostsLoading,
    error: boostsError,
    changeCount: boostChanges,
    reorder: reorderBoosts,
    remove: removeBoost,
    add: addBoosts,
    discard: discardBoosts,
    publish: publishBoosts
  } = useFeedBoosts(id)

  // --- settings form -------------------------------------------------------

  const {
    settings,
    changeCount: settingsChanges,
    update: updateSettings,
    reset: resetSettings,
    publish: publishSettings
  } = useFeedSettings(feed)

  const [saving, setSaving] = useState(false)

  /**
   * Apply commits the settings on its own, separately from the page's Publish.
   *
   * They are genuinely different concerns: settings describe what the feed IS,
   * while Publish commits what it CONTAINS and in what order. Making one wait on
   * the other meant a curator had to press two buttons to rename a feed.
   */
  const applySettings = async () => {
    if (!feed || !settingsChanges || saving) return
    setSaving(true)
    try {
      await publishSettings()
      hideSettings()
      showToast(
        t('contentCuration:settings:applied'),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      // Left open so the failed edits are still there to retry.
      showToast(
        mutationError?.message ?? t('contentCuration:settings:applyFailed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    } finally {
      setSaving(false)
    }
  }

  const closeSettings = () => {
    if (settingsChanges && !window.confirm(t('common:unsavedChanges'))) return
    resetSettings()
    hideSettings()
  }

  // --- publish -------------------------------------------------------------
  /**
   * The featured band has to be completely full to publish -- see the note on
   * HERO_SLOT_COUNT. It is normally curated from the band on Content home, but
   * its own page is still reachable by URL, so the rule lives on both routes
   * rather than on whichever one is easier to reach.
   *
   * Topic feeds are unaffected: TOPIC_FEED_MIN_ITEMS stays advisory.
   */
  const isIncompleteHero =
    feed?.slug === HERO_FEED_SLUG &&
    !boostsLoading &&
    boosts.length < HERO_SLOT_COUNT

  const publishBlockedReason = isIncompleteHero
    ? t('contentCuration:hero:publishBlocked', { required: HERO_SLOT_COUNT })
    : null

  // The bar commits content only, so it must not count settings -- those are
  // already saved by Apply. The navigation guard still watches both, because an
  // open modal can hold unsaved field edits.
  const changeCount = boostChanges
  const isDirty = boostChanges > 0 || settingsChanges > 0

  const publish = async () => {
    if (!feed || !boostChanges || saving) return
    if (isIncompleteHero) return
    setSaving(true)
    try {
      await publishBoosts()
      showToast(
        t('contentCuration:publish:published'),
        ToastType.INFO,
        SUCCESS_TOAST_MS
      )
    } catch (mutationError: any) {
      showToast(
        mutationError?.message ?? t('contentCuration:publish:failed'),
        ToastType.ERROR,
        ERROR_TOAST_MS
      )
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    resetSettings()
    discardBoosts()
  }

  // --- content picker ------------------------------------------------------

  // The hero feed is reachable from here too (Configure on Content home), and
  // it holds three rather than eight.
  const maxItems = maxItemsForFeed(feed?.slug)
  const remainingSlots = Math.max(0, maxItems - boosts.length)

  const confirmSelection = (picked: IContentFeedBoost[]) => {
    addBoosts(picked)
    closeSelect()
  }

  return {
    feed,
    loading,
    error,
    settings,
    updateSettings,
    isDirty,
    changeCount,
    publishBlockedReason,
    saving,
    applySettings,
    publish,
    discard,
    isSettingsOpen,
    openSettings,
    closeSettings,
    settingsChanges,
    boosts,
    boostsLoading,
    boostsError,
    reorderBoosts,
    removeBoost,
    isSelectOpen,
    openSelect,
    closeSelect,
    confirmSelection,
    remainingSlots,
    maxItems
  }
}

export default useContentFeedDetail
